import React, { Component } from 'react';
import { NavLink } from 'react-router-dom'
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

// Wrap AppSubmenu with forwardRef to handle ref forwarding
const AppSubmenuWithRef = React.forwardRef((props, ref) => {
    return <AppSubmenuClass {...props} forwardedRef={ref} />;
});

// Rename the original class for internal use
class AppSubmenuClass extends Component {

    static defaultProps = {
        className: null,
        items: null,
        onMenuItemClick: null,
        onRootItemClick: null,
        root: false,
        layoutMode: null,
        menuActive: false,
        parentMenuItemActive: false
    }

    static propTypes = {
        className: PropTypes.string,
        items: PropTypes.array,
        onMenuItemClick: PropTypes.func,
        onRootItemClick: PropTypes.func,
        root: PropTypes.bool,
        layoutMode: PropTypes.string,
        menuActive: PropTypes.bool,
        parentMenuItemActive: PropTypes.bool
    }

    constructor(props) {
        super(props);
        this.state = {
            activeIndex: null,
            permissions: localStorage.getItem("permissions").split(",")
        };
        this.submenuRefs = new Map();
    }

    onMenuItemClick(event, item, index) {
        //avoid processing disabled items
        if (item.disabled) {
            event.preventDefault();
            return true;
        }

        if (this.props.root && this.props.onRootItemClick) {
            this.props.onRootItemClick({
                originalEvent: event,
                item: item
            });
        }

        //execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
            event.preventDefault();
        }

        if (index === this.state.activeIndex)
            this.setState({ activeIndex: null });
        else
            this.setState({ activeIndex: index });

        if (this.props.onMenuItemClick) {
            this.props.onMenuItemClick({
                originalEvent: event,
                item: item
            });
        }
    }

    onKeyDown(event, item, index) {
        if (event.key === 'Enter') {
            this.onMenuItemClick(event, item, index);
        }
    }

    onMenuItemMouseEnter(index) {
        if (this.props.root && this.props.menuActive && this.isHorizontalOrSlim() && !this.isMobile()) {
            this.setState({ activeIndex: index });
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.parentMenuItemActive === false) {
            return {
                activeIndex: null
            }
        }

        return null;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.isHorizontalOrSlim() && !this.isMobile() && prevProps.menuActive && !this.props.menuActive) {
            this.setState({ activeIndex: null });
        }
    }

    isHorizontalOrSlim() {
        return (this.props.layoutMode === 'horizontal' || this.props.layoutMode === 'slim');
    }

    isMobile() {
        return window.innerWidth <= 640;
    }

    renderLinkContent(item) {
        let submenuIcon = item.items && <i className="fa fa-fw fa-angle-down menuitem-toggle-icon"></i>;
        let badge = item.badge && <span className="menuitem-badge">{item.badge}</span>;
        let content = <React.Fragment>
            <i className={item.icon}></i>
            <span>{item.label}</span>
            {submenuIcon}
            {badge}
        </React.Fragment>;

        return content;
    }

    renderLink(item, i) {
        let content = this.renderLinkContent(item);

        if (item.to) {
            return (
                <NavLink
                    to={item.to}
                    role="menuitem"
                    onClick={(e) => this.onMenuItemClick(e, item, i)}
                    end
                    target={item.target}
                    onMouseEnter={(e) => this.onMenuItemMouseEnter(i)}
                    className={({ isActive }) => isActive ? `active-menuitem-routerlink ${item.styleClass || ''}` : item.styleClass || ''}
                >
                    {content}
                </NavLink>
            )
        } else {
            return (
                <a href={item.url} tabIndex={item.url ? '' : 0} role="menuitem"
                    aria-haspopup={item.items ? 'true' : undefined}
                    aria-expanded={item.items ? (this.state.activeIndex === i) : undefined}
                    onClick={(e) => this.onMenuItemClick(e, item, i)} target={item.target}
                    onMouseEnter={(e) => this.onMenuItemMouseEnter(i)} onKeyDown={(e) => this.onKeyDown(e, item, i)} className={item.styleClass}>
                    {content}
                </a>
            );

        }
    }

    render() {
        var items = this.props.items && this.props.items.map((item, i) => {
            let active = this.state.activeIndex === i;
            let styleClass = classNames(item.badgeStyleClass, { 'active-menuitem': active });
            let tooltip = this.props.root && <div className="layout-menu-tooltip">
                <div className="layout-menu-tooltip-arrow"></div>
                <div className="layout-menu-tooltip-text">{item.label}</div>
            </div>;

            // Create or get ref for this menu item
            if (!this.submenuRefs.has(i)) {
                this.submenuRefs.set(i, React.createRef());
            }
            const submenuRef = this.submenuRefs.get(i);

            return <li className={styleClass} key={i} role="none">
                {item.items && this.props.root === true && <div className='arrow'></div>}
                {(this.state.permissions.includes(item.permission) || item.permission === undefined) && this.renderLink(item, i)}
                {tooltip}
                {item.items && (
                    <CSSTransition nodeRef={submenuRef} classNames="layout-submenu" timeout={{ enter: 1000, exit: 450 }} in={active} unmountOnExit>
                        <AppSubmenuWithRef ref={submenuRef} items={item.items} onMenuItemClick={this.props.onMenuItemClick}
                            layoutMode={this.props.layoutMode}
                            menuActive={this.props.menuActive} parentMenuItemActive={active} />
                    </CSSTransition>
                )}
            </li>
        });

        return items ? <ul ref={this.props.forwardedRef} role="menu" className={this.props.className}>{items}</ul> : null;
    }
}

export class AppMenu extends Component {

    static defaultProps = {
        model: null,
        onMenuItemClick: null,
        onRootMenuItemClick: null,
        layoutMode: null,
        active: false
    }

    static propTypes = {
        model: PropTypes.array,
        layoutMode: PropTypes.string,
        onMenuItemClick: PropTypes.func,
        onRootMenuItemClick: PropTypes.func,
        active: PropTypes.bool
    }

    render() {
        return <AppSubmenuWithRef items={this.props.model} className="layout-menu layout-main-menu clearfix"
            menuActive={this.props.active} onRootItemClick={this.props.onRootMenuItemClick}
            onMenuItemClick={this.props.onMenuItemClick} root={true} layoutMode={this.props.layoutMode}
            parentMenuItemActive={true} />
    }
}
